import { useCallback, useEffect, useMemo, useState } from "react";

import { SessionContext } from "@/lib/auth/context";
import {
  getSession,
  setSession as persistSession,
  clearSession as removeSession,
} from "@/lib/auth/session";
import { decodeJWT } from "@/lib/utils";

const MILLISECONDS_IN_SECOND = 1000;

export const SessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [session, setSession] = useState<Session | null>(null);

  const syncSession = useCallback(() => {
    const currentSession = getSession();
    setSession(currentSession);
  }, []);

  const clearSessionState = useCallback(() => {
    removeSession();
    setSession(null);
  }, []);

  const handleSetSession = useCallback(
    (nextSession: Session | null) => {
      if (!nextSession) {
        clearSessionState();
        return;
      }

      persistSession(nextSession);
      setSession(nextSession);
    },
    [clearSessionState]
  );

  useEffect(() => {
    syncSession();
  }, [syncSession]);

  useEffect(() => {
    const handleStorage = () => {
      syncSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncSession();
      }
    };

    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [syncSession]);

  useEffect(() => {
    if (!session) {
      return;
    }

    const decoded = decodeJWT(session.token);
    if (!decoded?.exp) {
      clearSessionState();
      return;
    }

    const expirationTime = decoded.exp * MILLISECONDS_IN_SECOND;
    const timeoutDuration = expirationTime - Date.now();
    if (timeoutDuration <= 0) {
      clearSessionState();
      return;
    }

    const timerId = window.setTimeout(clearSessionState, timeoutDuration);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [session, clearSessionState]);

  return (
    <SessionContext
      value={useMemo(
        () => ({
          session,
          setSession: handleSetSession,
        }),
        [session, handleSetSession]
      )}
    >
      {children}
    </SessionContext>
  );
};
